import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "🇺🇸 USA Shop", path: "/usa-shop" },
  { label: "Marketplace", path: "/marketplace" },
  { label: "Wholesale B2B", path: "/wholesale" },
  { label: "Buyer Board", path: "/buyer-requirements" },
  { label: "Social Feed", path: "/social" },
  { label: "Reseller & Stars", path: "/influencer" },
  { label: "Membership", path: "/membership" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isLoggedIn } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Lock body scroll when mobile search is open
  useEffect(() => {
    if (searchOpen) {
      document.body.style.overflow = "hidden";
      setTimeout(() => searchInputRef.current?.focus(), 100);
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [searchOpen]);

  const isHome = location.pathname === "/";

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    navigate("/");
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/marketplace?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery("");
    }
  };

  const roleBadgeColor: Record<string, string> = {
    buyer: "bg-orange-100 text-orange-700",
    seller: "bg-green-100 text-green-700",
    influencer: "bg-rose-100 text-rose-700",
  };

  const popularSearches = [
    "Wireless Earbuds", "Organic Cotton", "Bulk Order", "Korean Beauty",
    "Golf Cart", "Leather Handbag", "Yoga Mat", "Smart LED",
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || !isHome ? "bg-white shadow-sm border-b border-gray-100" : "bg-transparent"}`}>
        <div className="px-4 md:px-8 h-16 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
              alt="StarBookPrime Logo"
              className="h-10 w-10 rounded-lg object-cover"
            />
            <span className={`font-bold text-lg whitespace-nowrap hidden sm:block transition-colors ${scrolled || !isHome ? "text-gray-900" : "text-white"}`}>
              StarBook<span className="text-orange-500">Prime</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  location.pathname === link.path
                    ? "text-orange-500 bg-orange-50"
                    : scrolled || !isHome
                    ? "text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                    : "text-white/90 hover:text-white hover:bg-white/10"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-1.5 md:gap-2">
            {/* Search button */}
            <button
              onClick={() => setSearchOpen(true)}
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                scrolled || !isHome ? "text-gray-600 hover:bg-gray-100" : "text-white hover:bg-white/10"
              }`}
            >
              <i className="ri-search-line text-lg" />
            </button>

            {/* Notification */}
            <button
              className={`w-9 h-9 flex items-center justify-center rounded-full relative transition-colors cursor-pointer ${
                scrolled || !isHome ? "text-gray-600 hover:bg-gray-100" : "text-white hover:bg-white/10"
              }`}
            >
              <i className="ri-notification-3-line text-lg" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full" />
            </button>

            {/* Cart */}
            <Link
              to="/cart"
              className={`w-9 h-9 flex items-center justify-center rounded-full relative transition-colors cursor-pointer ${
                scrolled || !isHome ? "text-gray-600 hover:bg-gray-100" : "text-white hover:bg-white/10"
              }`}
            >
              <i className="ri-shopping-cart-line text-lg" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full" />
            </Link>

            {/* Sell Now — desktop only */}
            <Link
              to="/upload"
              className="hidden md:flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white text-sm font-semibold rounded-full hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-upload-2-line" />Sell Now
            </Link>

            {/* User menu — desktop only */}
            {isLoggedIn && user ? (
              <div className="relative hidden md:block" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-gray-200 bg-white hover:border-orange-300 transition-colors cursor-pointer"
                >
                  <img src={user.avatar} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
                  <span className="text-sm font-medium text-gray-800 max-w-[80px] truncate">{user.name.split(" ")[0]}</span>
                  <i className="ri-arrow-down-s-line text-gray-400 text-sm" />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-gray-50">
                      <p className="text-sm font-semibold text-gray-900 truncate">{user.name}</p>
                      <p className="text-xs text-gray-400 truncate">{user.email}</p>
                      <span className={`inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${roleBadgeColor[user.role]}`}>{user.role}</span>
                    </div>
                    <div className="py-1">
                      {user.role === "seller" && (
                        <>
                          <Link to="/dashboard" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                            <i className="ri-dashboard-line text-orange-500" />Seller Dashboard
                          </Link>
                          <Link to="/factory-dashboard" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                            <i className="ri-building-2-line text-orange-500" />Factory Dashboard
                          </Link>
                        </>
                      )}
                      <Link to="/upload" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                        <i className="ri-upload-2-line text-orange-500" />Upload Product
                      </Link>
                      {user.role === "influencer" && (
                        <Link to="/influencer" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                          <i className="ri-star-line text-rose-500" />Influencer Hub
                        </Link>
                      )}
                      <button onClick={handleLogout} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 cursor-pointer">
                        <i className="ri-logout-box-line" />Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/auth"
                className={`hidden md:flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full border transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled || !isHome
                    ? "border-gray-300 text-gray-700 hover:border-orange-500 hover:text-orange-500"
                    : "border-white/50 text-white hover:bg-white/10"
                }`}
              >
                Sign In
              </Link>
            )}

            {/* Hamburger — mobile only (lg:hidden) */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`lg:hidden w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                scrolled || !isHome ? "text-gray-600 hover:bg-gray-100" : "text-white hover:bg-white/10"
              }`}
            >
              <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`} />
            </button>
          </div>
        </div>

        {/* Desktop search bar (below nav, not full-screen) */}
        {searchOpen && (
          <div className="hidden md:block bg-white border-t border-gray-100 px-4 py-3">
            <form onSubmit={handleSearch}>
              <div className="max-w-2xl mx-auto flex items-center gap-2 bg-gray-50 rounded-full px-4 py-2 border border-gray-200">
                <i className="ri-search-line text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search products, sellers, categories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 bg-transparent text-sm outline-none text-gray-700 placeholder-gray-400"
                  autoFocus
                />
                {searchQuery && (
                  <button type="button" onClick={() => setSearchQuery("")} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                    <i className="ri-close-line" />
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        {/* Mobile menu drawer */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-3 flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === link.path ? "text-orange-500 bg-orange-50" : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-2 border-t border-gray-100 flex gap-2">
                <Link to="/upload" className="flex-1 text-center py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-full whitespace-nowrap">
                  Sell Now
                </Link>
                {isLoggedIn ? (
                  <button onClick={handleLogout} className="flex-1 py-2.5 border border-gray-300 text-red-500 text-sm font-semibold rounded-full whitespace-nowrap cursor-pointer">
                    Sign Out
                  </button>
                ) : (
                  <Link to="/auth" className="flex-1 text-center py-2.5 border border-gray-300 text-gray-700 text-sm font-semibold rounded-full whitespace-nowrap">
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* ── FULL-SCREEN MOBILE SEARCH OVERLAY ── */}
      {searchOpen && (
        <div className="md:hidden fixed inset-0 z-[60] bg-white flex flex-col">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-100">
            <form onSubmit={handleSearch} className="flex-1 flex items-center gap-3 bg-gray-50 rounded-2xl px-4 py-3 border border-gray-200">
              <i className="ri-search-line text-gray-400 text-lg flex-shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search products, factories, categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent text-sm outline-none text-gray-800 placeholder-gray-400"
              />
              {searchQuery && (
                <button type="button" onClick={() => setSearchQuery("")} className="text-gray-400 cursor-pointer flex-shrink-0">
                  <i className="ri-close-circle-fill text-lg" />
                </button>
              )}
            </form>
            <button
              onClick={() => { setSearchOpen(false); setSearchQuery(""); }}
              className="text-gray-500 font-semibold text-sm whitespace-nowrap cursor-pointer px-1"
            >
              Cancel
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-4 py-5">
            {!searchQuery ? (
              <>
                {/* Popular searches */}
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Popular Searches</p>
                <div className="flex flex-wrap gap-2 mb-8">
                  {popularSearches.map((term) => (
                    <button
                      key={term}
                      onClick={() => setSearchQuery(term)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-gray-50 border border-gray-200 text-sm text-gray-700 font-medium cursor-pointer hover:border-orange-300 hover:text-orange-600 transition-colors"
                    >
                      <i className="ri-search-line text-gray-400 text-xs" />
                      {term}
                    </button>
                  ))}
                </div>

                {/* Quick links */}
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Quick Links</p>
                <div className="flex flex-col gap-1">
                  {[
                    { icon: "ri-store-2-line", label: "USA Shop — Local Warehouse", path: "/usa-shop", color: "text-orange-500" },
                    { icon: "ri-ship-line", label: "Wholesale B2B — Bulk Orders", path: "/wholesale", color: "text-amber-600" },
                    { icon: "ri-file-list-3-line", label: "Buyer Board — Post Requirement", path: "/buyer-requirements", color: "text-green-600" },
                    { icon: "ri-global-line", label: "Global Marketplace", path: "/marketplace", color: "text-gray-600" },
                    { icon: "ri-vip-crown-line", label: "Membership — $8/mo", path: "/membership", color: "text-amber-500" },
                  ].map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSearchOpen(false)}
                      className="flex items-center gap-3 px-4 py-3.5 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className={`w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 flex-shrink-0 ${item.color}`}>
                        <i className={`${item.icon} text-base`} />
                      </div>
                      <span className="text-sm font-medium text-gray-800">{item.label}</span>
                      <i className="ri-arrow-right-s-line text-gray-300 ml-auto" />
                    </Link>
                  ))}
                </div>
              </>
            ) : (
              /* Search results hint */
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-orange-50 mb-4">
                  <i className="ri-search-line text-3xl text-orange-400" />
                </div>
                <p className="text-base font-bold text-gray-800 mb-1">Search for &ldquo;{searchQuery}&rdquo;</p>
                <p className="text-sm text-gray-400 mb-6">Press enter or tap the button below</p>
                <button
                  onClick={() => { if (searchQuery.trim()) { navigate(`/marketplace?q=${encodeURIComponent(searchQuery.trim())}`); setSearchOpen(false); setSearchQuery(""); } }}
                  className="flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm cursor-pointer"
                  style={{ background: "linear-gradient(135deg, #B8860B, #FFD700)", color: "#1a0f00" }}
                >
                  <i className="ri-search-line" /> Search Now
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
